import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { useTranslation } from 'react-i18next';
import employeeImportConfig from '@/../config/employee-import.json';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import Alert from '@/features/Quote/components/settings/Alert';
import { useExtractionUpload } from '@/features/Quote/hooks/useExtractionUpload';
import apiService from '@/services/apiService';
import { prepareExtractedEmployees } from '@/services/utils';
import type { Employee, ExtractionResult } from '@/types/employee';

registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

interface ImportProps {
	onImportComplete?: () => void;
	handleTriggerAppStatePolling?: () => void;
}

interface ImportResult {
	success: boolean;
	count: number;
	message: string;
}

const Import = ({ onImportComplete, handleTriggerAppStatePolling }: ImportProps) => {
	const { t } = useTranslation();
	const context = useAppContext();
	if (!context) {
		throw new Error('Import must be used within AppContextProvider');
	}
	const { premiumVarManagement } = context;
	const [importResult, setImportResult] = useState<ImportResult | null>(null);

	const importEmployees = async (
		employeeData: Employee[],
		fail: (message: string | null) => void,
		stop: () => void
	) => {
		try {
			// Clean up the extracted data to ensure API compatibility
			const cleanedEmployees = prepareExtractedEmployees(employeeData);

			// Replace existing employees using the premiumVarManagement system
			await premiumVarManagement.employees.replace(cleanedEmployees);

			setImportResult({
				success: true,
				count: cleanedEmployees.length,
				message: t('quotes.settings.employees.import.importSuccessMessage', {
					count: cleanedEmployees.length
				})
			});

			if (handleTriggerAppStatePolling) {
				handleTriggerAppStatePolling();
			}

			// Show brief success message before navigating back
			setTimeout(() => {
				if (onImportComplete) onImportComplete();
			}, 2000);
		} catch (err) {
			console.error('Import error:', err);
			fail(
				err instanceof Error
					? err.message
					: t('quotes.settings.employees.import.importErrorGeneric')
			);
		} finally {
			stop();
		}
	};

	const {
		files,
		setFiles,
		isProcessing,
		setError,
		error,
		warning,
		setWarning,
		filePondRef,
		handleProcessFile,
		clearPolling,
		stopProcessing
	} = useExtractionUpload<ExtractionResult>({
		upload: apiService.import.upload,
		fetchStatus: apiService.import.fetch,
		onCompleted: async (
			result,
			{ setWarning: warn, setError: fail, clearFiles, stopProcessing: stop }
		) => {
			if (result?.employee_list && result.employee_list.length > 0) {
				await importEmployees(result.employee_list, fail, stop);
			} else {
				warn(t('errors.noEmployeesFound'));
				stop();
				clearFiles();
			}
		}
	});

	return (
		<div data-testid="employees-import-view">
			<div className="flex items-center">
				<h6 className="font-bold text-lg mb-2">{t('quotes.settings.employees.import.title')}</h6>
			</div>

			{error && (
				<Alert
					variant="danger"
					className="mt-3"
					testId="employees-import-error-alert"
					icon={AlertTriangle}
					showIcon
					text={error}
				/>
			)}

			{warning && (
				<Alert
					variant="warning"
					className="mt-3"
					testId="employees-import-warning-alert"
					icon={AlertTriangle}
					showIcon
					text={warning}
				/>
			)}

			{!importResult?.success && !isProcessing && (
				<>
					<div className="leading-relaxed">
						<p className="text-muted-foreground font-medium mb-6">
							<span className="block mb-4">
								{t('quotes.settings.employees.import.description')}
							</span>
							<span className="block">{t('quotes.settings.employees.import.examplePayroll')}</span>
						</p>
					</div>

					<FilePond
						ref={filePondRef}
						{...employeeImportConfig}
						credits={false}
						files={files}
						onupdatefiles={setFiles}
						onprocessfiles={() => stopProcessing()}
						// Deliberately leaves the alerts alone: FilePond fires this for
						// programmatic removal too, so clearing here would wipe the message
						// the failure paths set just before calling clearFiles. onaddfile
						// clears them when the user tries again.
						onremovefile={() => {
							clearPolling();
							stopProcessing();
						}}
						onaddfile={(err) => {
							if (!err) {
								setError(null);
								setWarning(null);
								setImportResult(null);
							}
						}}
						server={
							{
								process: handleProcessFile,
								revert: null
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
							} as any
						}
						data-testid="employee-import-upload"
					/>

					<Button
						type="button"
						variant="outline"
						className="w-full sm:w-auto sm:ml-auto"
						onClick={onImportComplete}
						data-testid="employees-import-back-button"
						icon={<ArrowLeft className="h-4 w-4" />}
					>
						{t('quotes.settings.employees.import.backButton')}
					</Button>
				</>
			)}

			{importResult?.success && (
				<Alert variant="success" className="mt-3">
					<div className="flex items-center">
						<Spinner size="sm" variant="current" className="mr-3" visible={true} />
						<div>
							<div className="font-medium">{importResult.message}</div>
							<small className="text-muted-foreground">
								{t('quotes.settings.employees.import.employeesAdding')}
							</small>
						</div>
					</div>
				</Alert>
			)}

			{isProcessing && !importResult?.success && (
				<Alert variant="info" className="mt-3">
					<div className="flex items-center">
						<Spinner size="sm" variant="current" className="mr-3" visible={isProcessing} />
						<div>
							<div className="font-medium">{t('common.fileProcessing')}</div>
							<small className="text-muted-foreground">
								{t('quotes.settings.employees.import.fileAnalyzing')}
							</small>
						</div>
					</div>
				</Alert>
			)}
		</div>
	);
};

export default Import;
